import {
  ApiPayorReportReviewStatusChoices,
  ApiPayorReportSubmissionStatusChoices,
  ApiReportReportStatusChoices,
  ReportType,
} from "@api/graphql/types-and-hooks";
import { ReportNode } from "@providers/ReportProvider";

export type ReportLifecycleStatus = "Draft" | "Complete" | "Submitted" | "Needs Revision" | "Approved";

/**
 * Whether a report has reached the terminal `Completed` (signed) state. Reads the
 * base `reportStatus` only — this is the "is the report locked / signed" check,
 * distinct from the display-oriented `getReportLifecycleStatus` (which also folds
 * in `Generating` and payor submission/review state).
 */
export const isReportCompleted = (report?: { reportStatus?: ApiReportReportStatusChoices | null } | null): boolean =>
  report?.reportStatus === ApiReportReportStatusChoices.Completed;

type SubmissionNode = NonNullable<
  NonNullable<NonNullable<ReportType["payorreportsubmissionSet"]>["edges"][number]>["node"]
>;
type ReviewNode = NonNullable<NonNullable<SubmissionNode["reviews"]["edges"][number]>["node"]>;

/**
 * Finds the latest non-FAILED submission and its latest review for a report.
 * Shared by `getReportLifecycleStatus` (badge) and `getLatestPayorReview`
 * (banner) so both surfaces read the same review.
 *
 *   - FAILED submissions are filtered out: failure is an event, not a state,
 *     and a FAILED row should never be the latest-by-submittedAt (otherwise a
 *     Needs-Revision report with a later FAILED attempt would drop back to
 *     base reportStatus and lose its real status).
 *   - A null `submittedAt` means a freshly-created INITIALIZED submission
 *     whose timestamp the backend hasn't populated yet; during a resubmission
 *     this row is the newest one, so we sort it ahead of any older submission
 *     with a real timestamp.
 *   - Each submission may have at most one review; we still take the latest
 *     by `createdDate` defensively.
 */
const findLatestSubmissionAndReview = (
  report?: ReportNode,
): { submission: SubmissionNode; review: ReviewNode | null } | null => {
  const submissions = (report?.payorreportsubmissionSet?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is SubmissionNode => !!node)
    .filter((node) => node.status !== ApiPayorReportSubmissionStatusChoices.Failed);

  const submissionRank = (submittedAt: string | null | undefined): number =>
    submittedAt ? new Date(submittedAt).getTime() : Number.POSITIVE_INFINITY;
  const submission = submissions
    .slice()
    .sort((a, b) => submissionRank(b.submittedAt) - submissionRank(a.submittedAt))[0];
  if (!submission) return null;

  const review =
    (submission.reviews?.edges ?? [])
      .map((edge) => edge?.node)
      .filter((node): node is ReviewNode => !!node)
      .slice()
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())[0] ?? null;

  return { submission, review };
};

/**
 * Single source of truth for the displayed lifecycle status of a report.
 *
 * Backend manages the lifecycle; this helper just reads.
 *
 *   1. Every report has a base `reportStatus` of Draft or Completed (mapped to
 *      "Draft" / "Complete").
 *   2. Payor reports continue past Complete via PayorReportSubmissionSet, whose
 *      latest entry (sorted by submittedAt) carries Submitted / Needs Revision /
 *      Approved. When a submission exists, it overrides the base status — the
 *      report.reportStatus may flip back to Draft mid-edit, but the displayed
 *      status follows the latest submission/review.
 *   3. Review status, when present, takes precedence over submission status.
 *
 * No backend mutation depends on this — it's purely the display surface used by
 * the table, the detail-page header, and any badge that needs the lifecycle.
 */
export const getReportLifecycleStatus = (report?: ReportNode): ReportLifecycleStatus => {
  const latest = findLatestSubmissionAndReview(report);

  if (latest) {
    if (latest.review?.status === ApiPayorReportReviewStatusChoices.Approved) return "Approved";
    if (latest.review?.status === ApiPayorReportReviewStatusChoices.NeedsRevision) return "Needs Revision";

    switch (latest.submission.status) {
      case ApiPayorReportSubmissionStatusChoices.Approved:
        return "Approved";
      case ApiPayorReportSubmissionStatusChoices.NeedsRevision:
        return "Needs Revision";
      case ApiPayorReportSubmissionStatusChoices.Initialized:
      case ApiPayorReportSubmissionStatusChoices.InProgress:
      case ApiPayorReportSubmissionStatusChoices.Completed:
        return "Submitted";
      default:
        break;
    }
  }

  // Generating is the transient backend state while the export workflow
  // finalizes after the user clicks Complete — surface it as "Complete" so
  // the report doesn't visually drop back to Draft while the export polls.
  return report?.reportStatus === ApiReportReportStatusChoices.Completed ||
    report?.reportStatus === ApiReportReportStatusChoices.Generating
    ? "Complete"
    : "Draft";
};

export interface LatestPayorReview {
  status: ApiPayorReportReviewStatusChoices;
  feedback: string | null;
  feedbackFilePath: string | null;
  feedbackFileSignedUrl: string | null;
}

/**
 * Returns the latest payor review on a report — the one driving the Needs
 * Revision / Approved status. Returns null when no submission has a review,
 * or when the latest submission is filtered out as FAILED.
 */
export const getLatestPayorReview = (report?: ReportNode): LatestPayorReview | null => {
  const latest = findLatestSubmissionAndReview(report);
  if (!latest?.review) return null;

  return {
    status: latest.review.status,
    feedback: latest.review.feedback ?? null,
    feedbackFilePath: latest.review.feedbackFilePath ?? null,
    feedbackFileSignedUrl: latest.review.feedbackFileSignedUrl ?? null,
  };
};
